<template>
  <div class="navbar-main">
    <ul class="navbar-main-items">
      <li class="nav-item">
        <GDropdown
          class="nav-events-btn"
          variant="outline-primary"
          right
          split
          :class="{ 'is-parent-active': isLinkActive }"
        >
          <template v-slot:button-content>
            <router-link :to="{ name: 'events-overview' }" :class="{ 'is-parent-active': isLinkActive }">
              <Icon type="events" size="24px" class="icon" />
              <span class="nav-item-text">{{ $t('navigation.events') }}</span>
            </router-link>
          </template>
          <GDropdownItem v-if="hasOrganizationPermissions('event.read')" data-test-templates-link class="dropdown-item">
            <router-link :to="{ name: 'templates-overview' }">
              {{ $t('navigation.templates') }}
            </router-link>
          </GDropdownItem>
          <GDropdownItem
            v-if="hasOrganizationPermissions('schedule.read')"
            :to="{ name: 'schedules-overview' }"
            data-test-schedules-link
            class="dropdown-item"
          >
            <router-link :to="{ name: 'schedules-overview' }">
              {{ $t('navigation.schedules') }}
            </router-link>
          </GDropdownItem>
          <GDropdownItem
            v-if="hasOrganizationPermissions('gate.write')"
            :to="{ name: 'gates-overview' }"
            data-test-gates-link
            class="dropdown-item"
          >
            <router-link :to="{ name: 'gates-overview' }">
              {{ $t('navigation.gates') }}
            </router-link>
          </GDropdownItem>
        </GDropdown>
      </li>
      <li class="nav-item">
        <router-link v-if="hasOrganizationExportPermission()" :to="{ name: 'reports-overview' }" data-test-reports-link>
          <Icon type="reports" size="24px" class="icon" />
          <span class="nav-item-text">{{ $t('navigation.reports') }}</span>
        </router-link>
      </li>
      <li class="nav-item">
        <router-link
          v-if="hasOrganizationPermissions('ticket.price.read')"
          :to="{ name: 'prices-overview' }"
          data-test-prices-link
        >
          <Icon type="prices" size="24px" class="icon" />
          <span class="nav-item-text">{{ $t('navigation.prices') }}</span>
        </router-link>
      </li>
      <li class="nav-item">
        <router-link
          v-if="hasOrganizationPermissions('attendee.read')"
          :to="{ name: 'customers' }"
          data-test-attendees-link
        >
          <Icon type="attendees" size="24px" class="icon" />
          <span class="nav-item-text">{{ $t('navigation.customers') }}</span>
        </router-link>
      </li>
      <li class="nav-item">
        <router-link
          v-if="hasOrganizationPermissions('event.event.scan')"
          :to="{ name: 'organization-scanning' }"
          data-test-scanning-link
        >
          <Icon type="scanning" size="24px" class="icon" />
          <span class="nav-item-text">{{ $t('navigation.scanning') }}</span>
        </router-link>
      </li>
      <li class="nav-item item-create-event">
        <GDropdown
          v-if="hasOrganizationPermissions('event.create')"
          variant="outline-primary"
          size="sm"
          right
          split
          @click="$router.push({ name: 'event-create' })"
        >
          <template v-slot:button-content>
            <span data-test-create-event-button>
              {{ $t('views.events.create.title') }}
            </span>
          </template>
          <GDropdownItem @click="$router.push({ name: 'event-create' })">
            {{ $t('views.events.create.new') }}
          </GDropdownItem>
          <GDropdownItem
            v-if="hasOrganizationPermissions('event.create')"
            data-test-create-from-template-option
            @click="openCreateFromTemplate"
          >
            {{ $t('views.events.create.fromTemplate') }}
          </GDropdownItem>
        </GDropdown>
      </li>
    </ul>
    <CreateFromTemplateModal
      v-if="hasOrganizationPermissions('event.create')"
      data-test-create-from-template-modal
      :isOpen="isCreateFromTemplateModalOpen"
      @onClose="closeCreateFromTemplate"
    />
  </div>
</template>

<script lang="ts">
  import { defineAsyncComponent, defineComponent } from 'vue';
  import PermissionsMixin from '@/mixins/PermissionsMixin';
  import Icon from '@/components/Icon.vue';

  export default defineComponent({
    components: {
      Icon,
      CreateFromTemplateModal: defineAsyncComponent(() => import('@/components/modals/CreateFromTemplate.vue')),
    },
    mixins: [PermissionsMixin],
    data(): { isCreateFromTemplateModalOpen: boolean } {
      return {
        isCreateFromTemplateModalOpen: false,
      };
    },
    computed: {
      isLinkActive() {
        const { path } = this.$route;
        return path.match(/schedules|events|templates|gates(?=\/)/gi);
      },
    },
    methods: {
      openCreateFromTemplate() {
        this.isCreateFromTemplateModalOpen = true;
      },
      closeCreateFromTemplate() {
        this.isCreateFromTemplateModalOpen = false;
      },
    },
  });
</script>

<style lang="scss" scoped>
  .navbar-main-items {
    width: 100%;
    display: flex;
    flex-wrap: nowrap;
    justify-content: flex-start;
    align-items: center;
    padding: 0;
    margin: 0;
    list-style: none;
  }

  // This line is actually being used and eslint is complaining about it
  // eslint-disable-next-line vue-scoped-css/no-unused-selector
  .nav-item a {
    display: flex;
    align-items: center;
    padding: 0.25rem;
    margin-right: 0.25rem;
    white-space: nowrap;
    color: $color-gray-300;

    .icon :deep(path) {
      fill: $color-gray-300;
    }

    &:hover,
    &.is-active,
    &.is-parent-active {
      color: $color-primary;

      .icon :deep(path) {
        fill: $color-primary;
      }
    }
  }

  .nav-item-text {
    display: none;
    padding-left: 0.15rem;

    @media (min-width: $tablet) {
      display: block;
    }
  }

  .nav-events-btn li a {
    color: $color-black;
    &:hover {
      color: $color-black;
    }
    // add selector for mousedown and make bg primary and color white
  }
  .dropdown-item:focus-within:hover {
    background-color: $color-primary;
    color: white !important;

    :deep(a) {
      color: $color-white;
      &:hover {
        color: $color-white;
      }
    }
  }

  // eslint-disable-next-line vue-scoped-css/no-unused-selector
  .nav-events-btn {
    :deep(button) {
      border: 0 !important;
      padding-left: 0 !important;
      padding-right: 0 !important;
    }

    &:hover {
      :deep(button) {
        background-color: transparent !important;
      }
    }

    :deep(svg) {
      fill: $color-gray-300;
    }

    &:hover,
    &.is-active,
    &.is-parent-active {
      :deep(svg) {
        fill: $color-primary;
      }
    }
  }

  .nav-events-btn {
    padding-right: 0;

    :deep(button) {
      background-color: transparent;
      border: none;
      font-size: $size-normal;
      padding: 0;
    }
  }

  .item-create-event {
    margin-left: auto;

    @media (min-width: $tablet) {
      display: block;
    }
  }
</style>
